import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../contexts/ToastContext';
import { formatPlanPrice } from '../../utils/planPrice';

const TITLE_KEYS = {
  default: 'modal.titleDefault',
  budget: 'modal.titleBudgetLimit',
  wallet: 'modal.titleWalletLimit',
  ai: 'modal.titleAiLimit',
  renewal: 'modal.titleRenewal',
};

const INTERVALS = ['monthly', 'yearly', 'triennial'];
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 200; // ~10 minutes of polling at 3s intervals

function UpgradeModal() {
  const { t } = useTranslation('subscription');
  const { plan, subscription, upgradeReason, closeUpgradeModal, checkout, checkPaymentStatus, saveCheckoutAsExpense, syncAfterPayment } = useSubscription();
  const { addToast } = useToast();
  const [interval, setIntervalValue] = useState('monthly');
  const [payment, setPayment] = useState(null);
  const [polling, setPolling] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState('');
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const pollCountRef = useRef(0);

  useEffect(() => {
    setError('');
    setPayment(null);
    setTimedOut(false);
    setPaymentSucceeded(false);
    // Renewing keeps the plan the user already has instead of defaulting to monthly.
    setIntervalValue(upgradeReason === 'renewal' && subscription ? subscription.interval : 'monthly');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgradeReason]);

  useEffect(() => {
    if (!payment) return undefined;
    pollCountRef.current = 0;
    setPolling(true);
    setTimedOut(false);

    const poll = async () => {
      pollCountRef.current += 1;
      try {
        const status = await checkPaymentStatus(payment.paymentId);
        if (status === 'succeeded') {
          setPolling(false);
          await syncAfterPayment();
          addToast(t('modal.success'), 'success');
          setPaymentSucceeded(true);
          return;
        }
        if (status === 'failed') {
          setPolling(false);
          setError(t('modal.error'));
          return;
        }
      } catch (pollError) {
        // Transient network hiccup - keep polling rather than surfacing every blip.
      }
      if (pollCountRef.current >= MAX_POLLS) {
        setPolling(false);
        setTimedOut(true);
        return;
      }
      timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    };

    let timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment]);

  if (!upgradeReason) return null;

  const summary = plan[interval] || { price: 0, salePrice: null };
  const hasSale = plan.saleActive && summary.salePrice != null;

  const startCheckout = async () => {
    setError('');
    const result = await checkout(interval);
    if (!result.success) {
      setError(result.error || t('modal.error'));
      return;
    }
    setPayment(result.payment);
  };

  const checkNow = async () => {
    if (!payment) return;
    setTimedOut(false);
    try {
      const status = await checkPaymentStatus(payment.paymentId);
      if (status === 'succeeded') {
        await syncAfterPayment();
        addToast(t('modal.success'), 'success');
        setPaymentSucceeded(true);
        return;
      }
    } catch (checkError) {
      // fall through to re-showing the "still waiting" state below
    }
    setTimedOut(true);
  };

  const saveAsExpense = async () => {
    setSavingExpense(true);
    const result = await saveCheckoutAsExpense(payment.paymentId);
    setSavingExpense(false);
    addToast(result.success ? t('qr.saveExpenseSaved') : (result.error || t('qr.saveExpenseError')), result.success ? 'success' : 'error');
    closeUpgradeModal();
  };

  return (
    <div className="modal-overlay upgrade-modal" onClick={closeUpgradeModal}>
      <div className="card modal-content" onClick={(event) => event.stopPropagation()}>
        <h3>{t(TITLE_KEYS[upgradeReason] || TITLE_KEYS.default)}</h3>

        {!plan.paymentsAvailable ? (
          <div className="coming-soon-panel">
            <p>{t('comingSoon.message')}</p>
            <ul className="upgrade-feature-list">
              <li>{t('modal.featureBudgets')}</li>
              <li>{t('modal.featureWallets')}</li>
              <li>{t('modal.featureAi')}</li>
            </ul>
          </div>
        ) : !payment ? (
          <>
            <p>{t('modal.subtitle')}</p>

            <div className="interval-selector">
              {INTERVALS.map((key) => (
                <button
                  type="button"
                  key={key}
                  className={`interval-option${interval === key ? ' active' : ''}`}
                  onClick={() => setIntervalValue(key)}
                >
                  {t(`interval.${key}Label`)}
                </button>
              ))}
            </div>

            <div className="upgrade-modal-price">
              {hasSale && <span className="price-original">{formatPlanPrice(summary.price, plan.currency)}</span>}
              <strong>{formatPlanPrice(hasSale ? summary.salePrice : summary.price, plan.currency)}</strong>
              <span className="price-period">{t(`interval.${interval}Period`)}</span>
              {hasSale && <span className="plan-badge plan-pro">{plan.saleLabel || t('modal.saleBadge')}</span>}
            </div>

            <ul className="upgrade-feature-list">
              <li>{t('modal.featureBudgets')}</li>
              <li>{t('modal.featureWallets')}</li>
              <li>{t('modal.featureAi')}</li>
            </ul>

            <button type="button" className="primary-btn standout-btn qr-generate-btn" onClick={startCheckout}>
              {t('qr.generateButton')}
            </button>
            {error && <span className="error-text">{error}</span>}
          </>
        ) : paymentSucceeded ? (
          <div className="qr-payment-panel save-expense-panel">
            <p>{t('qr.saveExpensePrompt')}</p>
            <div className="modal-actions">
              <button type="button" className="text-btn" disabled={savingExpense} onClick={closeUpgradeModal}>
                {t('qr.saveExpenseNo')}
              </button>
              <button type="button" className="primary-btn standout-btn" disabled={savingExpense} onClick={saveAsExpense}>
                {savingExpense ? t('qr.saveExpenseSaving') : t('qr.saveExpenseYes')}
              </button>
            </div>
          </div>
        ) : (
          <div className="qr-payment-panel">
            <p>{t('qr.instructions')}</p>
            <img src={payment.qrUrl} alt={t('qr.imageAlt')} className="qr-payment-image" />
            <div className="qr-payment-details">
              <div className="billing-summary-row">
                <span>{t('qr.amountLabel')}</span>
                <strong>{formatPlanPrice(payment.amount, payment.currency)}</strong>
              </div>
              <div className="billing-summary-row">
                <span>{t('qr.contentLabel')}</span>
                <strong className="qr-transfer-content">{payment.transferContent}</strong>
              </div>
            </div>
            {polling && <p className="qr-waiting"><span className="qr-spinner" aria-hidden="true" />{t('qr.waiting')}</p>}
            {timedOut && (
              <>
                <p className="qr-waiting">{t('qr.timedOut')}</p>
                <button type="button" className="text-btn" onClick={checkNow}>{t('qr.checkNow')}</button>
              </>
            )}
            {error && <span className="error-text">{error}</span>}
          </div>
        )}

        {!paymentSucceeded && (
          <div className="modal-actions">
            <button type="button" className="text-btn" onClick={closeUpgradeModal}>{t('modal.cancelAction')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default UpgradeModal;
